import { useCanGoBack, useNavigate, useParams, useRouter } from '@tanstack/react-router';
import { ArrowLeft, MessageCircle } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useDiscussion } from '@/lib/discussions/queries';
import { DiscussionThread } from '@/features/discussions/discussion-thread';

/**
 * Écran d'une discussion — enfant DIRECT de `appRoute` (mirroir exact
 * `_app.rendez-vous.$reservationId.tsx`), accessible tuteur ET élève
 * participant : la RLS `discussions: select_participant` fait le reste.
 */
export function DiscussionThreadPage() {
  const { discussionId } = useParams({ strict: false }) as { discussionId: string };
  const { user, profile } = useAuth();
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const navigate = useNavigate();
  const { data: discussion, isLoading } = useDiscussion(discussionId);

  const listRoute = profile?.role === 'student' ? '/student/discussions' : '/tutor/discussions';

  function handleBack() {
    if (canGoBack) router.history.back();
    else void navigate({ to: listRoute });
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="border-primary-400 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
      </div>
    );
  }

  if (!discussion || !user) {
    return (
      <div className="mx-auto max-w-sm px-4 py-6 sm:py-10">
        <button
          type="button"
          onClick={handleBack}
          aria-label="Retour"
          className="border-ink-200 text-ink-700 hover:bg-ink-50 mb-3 flex h-11 w-11 items-center justify-center rounded-full border bg-white shadow-sm transition-colors active:scale-95"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="py-10 text-center">
          <MessageCircle className="text-ink-300 mx-auto h-8 w-8" />
          <p className="text-ink-700 mt-2 font-sans text-sm font-semibold">
            Discussion introuvable
          </p>
          <p className="text-ink-500 mt-1 font-sans text-xs">
            Ce lien n'est plus valide, ou tu n'as pas accès à cette discussion.
          </p>
        </div>
      </div>
    );
  }

  const myRole: 'tutor' | 'student' = user.id === discussion.tutorId ? 'tutor' : 'student';

  return (
    // Écran FIXÉ au viewport : la page ne défile jamais (sur Chrome iOS une
    // colonne `h-dvh` laissait la page défiler et la saisie remontait au milieu
    // de l'écran) ; seule la liste des messages défile, la saisie reste en bas.
    // Barre de statut / Dynamic Island en haut (natif), indicateur d'accueil en
    // bas (géré par la barre de saisie du fil).
    <div
      data-testid="discussion-screen"
      className="native:pt-[env(safe-area-inset-top)] fixed inset-0 z-10 flex flex-col overscroll-none"
    >
      <div className="mx-auto w-full max-w-2xl shrink-0 px-4 pt-4 sm:pt-6">
        <button
          type="button"
          onClick={handleBack}
          aria-label="Retour"
          className="border-ink-200 text-ink-700 hover:bg-ink-50 mb-1 flex h-11 w-11 items-center justify-center rounded-full border bg-white shadow-sm transition-colors active:scale-95"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
      </div>
      <DiscussionThread discussion={discussion} myUserId={user.id} myRole={myRole} />
    </div>
  );
}
